import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';

const MAX_NAME_LENGTH = 100;

interface NameModalProps {
    isOpen: boolean;
    initialName: string;
    onClose: () => void;
    onSave: (fullname: string) => Promise<void> | void;
}

const NameModal: React.FC<NameModalProps> = ({ isOpen, initialName, onClose, onSave }) => {
    // Defensive trim: legacy DB rows could have leading/trailing whitespace,
    // and we don't want canSave to mistake a whitespace-only fix for a no-op.
    const initialTrimmed = initialName.trim();
    const [value, setValue] = useState(initialTrimmed);
    const [saving, setSaving] = useState(false);

    // Reset to the latest server value every time the modal opens, so a
    // previously cancelled draft never leaks into a fresh edit session.
    useEffect(() => {
        if (isOpen) {
            setValue(initialTrimmed);
            setSaving(false);
        }
    }, [isOpen, initialTrimmed]);

    useEscapeKey(onClose, isOpen && !saving);

    if (!isOpen) return null;

    const trimmed = value.trim();
    const canSave = !!trimmed && trimmed !== initialTrimmed && trimmed.length <= MAX_NAME_LENGTH;

    const submit = async () => {
        if (!canSave || saving) return;
        setSaving(true);
        try {
            await onSave(trimmed);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[520px] p-6 relative">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Edit name</h3>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50"
                        aria-label="Close modal"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Full name</label>
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submit();
                            }}
                            maxLength={MAX_NAME_LENGTH}
                            autoFocus
                            disabled={saving}
                            placeholder="Enter your full name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm disabled:opacity-60"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            {value.length}/{MAX_NAME_LENGTH}
                        </p>
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={!canSave || saving}
                        onClick={submit}
                        className={`px-4 py-2 text-sm font-bold text-white rounded-lg inline-flex items-center gap-2 ${
                            canSave && !saving
                                ? 'bg-gray-800 hover:bg-gray-700'
                                : 'bg-gray-300 cursor-not-allowed'
                        }`}
                    >
                        {saving && (
                            <span className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        )}
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NameModal;
